import { useEffect, useRef, useState } from 'react';
import { TRAINING_FAQ } from '../data/trainingFaq';

export default function FaqPanel() {
  const [open, setOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(
    TRAINING_FAQ[0]?.id ?? '',
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const activeSection =
    TRAINING_FAQ.find((s) => s.id === activeSectionId) ?? TRAINING_FAQ[0];

  const jumpToSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Tips til træning"
        title="Tips til træning"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-700 ring-1 ring-zinc-200 transition hover:bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10 dark:hover:bg-zinc-700"
      >
        <InfoIcon />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-10 backdrop-blur-sm sm:pt-12"
          role="dialog"
          aria-modal="true"
          aria-labelledby="faq-modal-title"
          onClick={() => setOpen(false)}
        >
          <div
            className="mb-8 flex h-[min(85vh,42rem)] w-full max-w-3xl flex-col animate-fade-in-up rounded-2xl bg-white shadow-xl ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-zinc-200 px-5 py-4 dark:border-white/10">
              <div>
                <h2
                  id="faq-modal-title"
                  className="text-xl font-semibold text-strong"
                >
                  Sådan får du mest ud af træningen
                </h2>
                <p className="mt-1 text-xs text-faint">
                  Tips til ti-fingersystemet og TouchType
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-secondary shrink-0"
                aria-label="Luk"
              >
                Luk
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
              <nav
                className="shrink-0 overflow-x-auto border-b border-zinc-200 px-3 py-2 sm:w-44 sm:border-b-0 sm:border-r sm:py-3 dark:border-white/10"
                aria-label="FAQ-emner"
              >
                <ul className="flex gap-1 sm:flex-col">
                  {TRAINING_FAQ.map((section) => {
                    const active = section.id === activeSectionId;
                    return (
                      <li key={section.id} className="shrink-0 sm:shrink">
                        <button
                          type="button"
                          onClick={() => jumpToSection(section.id)}
                          className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-left text-xs font-medium transition sm:w-full sm:whitespace-normal ${
                            active
                              ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/40'
                              : 'text-muted hover:bg-zinc-50 hover:text-strong dark:hover:bg-zinc-800/60'
                          }`}
                          aria-current={active ? 'true' : undefined}
                        >
                          {section.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
              >
                {activeSection && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-semibold text-strong">
                      {activeSection.title}
                    </h3>
                    {activeSection.items.map((item) => (
                      <article key={item.id} className="space-y-2">
                        <h4 className="text-sm font-medium text-strong">
                          {item.question}
                        </h4>
                        <div className="space-y-2 text-sm leading-relaxed text-muted">
                          {item.paragraphs.map((p, i) => (
                            <p key={i}>{p}</p>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InfoIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}
